// // // import from_px from "@pdfking/workflow/units/from_px"
// import create_blob from "@pdfking/workflow/utils/create_blob"
import { StringToUInt8, ToBlob } from "@pdfking/tfunc"

import type { PDFDocument } from "../types/PDFDocument"
import { GetLastError } from "./GetLastError"

export const make_float_array = (pdfium, pointer, length = 1) =>
  new Float32Array(pdfium.pdfium.HEAPU8.buffer, pointer, length)

export const get_page_box = (pdfium, page, box_function): number[] => {
  // If the function doesn't exist in this PDFium build, return null

  // Allocate memory for the float values (4 bytes each)
  const pointers = [
    pdfium.pdfium.wasmExports.malloc(4),
    pdfium.pdfium.wasmExports.malloc(4),
    pdfium.pdfium.wasmExports.malloc(4),
    pdfium.pdfium.wasmExports.malloc(4),
  ]

  // Initialize the memory with zeros

  // Call the function with the allocated pointers
  const success = box_function(page, ...pointers)

  if (!success) {
    return
  }

  const points = pointers.map(p => make_float_array(pdfium, p)[0])

  pointers.forEach(pdfium.pdfium.wasmExports.free)

  return points
}

// Helper function to extract a single page as a PDF blob
export const ExtractPage = async (
  { pdfium, doc_pointer }: PDFDocument,
  page: number
) => {
  // Create a new PDF document for the single page
  const new_doc_pointer = pdfium.FPDF_CreateNewDocument()
  if (!new_doc_pointer) {
    throw GetLastError(pdfium)
  }

  // Import the page from source document
  // Using pagerange string as per the function signature
  const page_range = `${page}` // 1-based page index in page range string

  // Convert JavaScript string to C string
  const page_range_byte_size = StringToUInt8(page_range).length + 1
  const pageRangePtr = pdfium.pdfium.wasmExports.malloc(page_range_byte_size)

  pdfium.pdfium.stringToUTF8(page_range, pageRangePtr, page_range_byte_size)

  const import_operation = pdfium.FPDF_ImportPages(
    new_doc_pointer,
    doc_pointer,
    pageRangePtr,
    0 // Index where to insert in destination doc (0 for empty doc)
  )

  pdfium.pdfium.wasmExports.free(pageRangePtr)

  if (!import_operation) {
    throw GetLastError(pdfium)
  }

  // Load the imported page to get dimensions
  const imported_page = pdfium.FPDF_LoadPage(new_doc_pointer, 0)
  if (!imported_page) {
    throw GetLastError(pdfium)
  }

  // Get page dimensions for default box values
  // const width = pdfium.FPDF_GetPageWidthF(imported_page)
  // const height = pdfium.FPDF_GetPageHeightF(imported_page)
  // Get page size using FPDF_GetPageSizeByIndexF
  // Allocate memory for the size struct (8 bytes for two floats)
  const size_pointer = pdfium.pdfium.wasmExports.malloc(8)

  // Call FPDF_GetPageSizeByIndexF
  const size_success = pdfium.FPDF_GetPageSizeByIndexF(
    doc_pointer, // Original document
    page - 1, // Page index in original document
    size_pointer // Pointer to size struct
  )

  if (!size_success) {
    pdfium.pdfium.wasmExports.free(size_pointer)
    throw GetLastError(pdfium)
  }

  const [width, height] = make_float_array(pdfium, size_pointer, 2)

  // Create media box from the dimensions
  const media_box = [0, 0, width, height]

  // Free allocated memory
  pdfium.pdfium.wasmExports.free(size_pointer)

  const trim_box =
    get_page_box(pdfium, imported_page, pdfium.FPDFPage_GetTrimBox) || media_box

  // Size buffers already freed above
  pdfium.FPDF_ClosePage(imported_page)

  // Create a buffer to store the PDF data
  let pdf_buffer = new Uint8Array(0)

  // Create a write callback function
  const write_callback = (_, data, size) => {
    // Create a new buffer with the combined size
    const new_buffer = new Uint8Array(pdf_buffer.length + size)

    // Copy existing data
    new_buffer.set(pdf_buffer)

    // Copy new data from PDFium's memory
    const new_data = new Uint8Array(
      pdfium.pdfium.HEAPU8.buffer,
      pdfium.pdfium.HEAPU8.byteOffset + data,
      size
    )
    new_buffer.set(new_data, pdf_buffer.length)

    // Update the buffer
    pdf_buffer = new_buffer

    return size
  }

  // Add the callback function to PDFium's function table
  const write_callback_pointer = pdfium.pdfium.addFunction(
    write_callback,
    "iiii"
  )

  // Create FPDF_FILEWRITE structure
  // Struct has 2 fields: version (int) and WriteBlock function pointer
  const file_write_struct = pdfium.pdfium.wasmExports.malloc(8) // 4 bytes for version + 4 bytes for function pointer

  // Set version = 1
  pdfium.pdfium.setValue(file_write_struct, 1, "i32")

  // Set WriteBlock function pointer
  pdfium.pdfium.setValue(file_write_struct + 4, write_callback_pointer, "i32")

  // Save the PDF using the callback
  // Using FPDF_NO_INCREMENTAL (2) instead of 0 or FPDF_INCREMENTAL (1)
  // to avoid timestamp-related issues
  const result = pdfium.FPDF_SaveAsCopy(
    new_doc_pointer,
    file_write_struct,
    2 // FPDF_NO_INCREMENTAL
  )

  if (!result) {
    throw GetLastError(pdfium)
  }

  const blob = ToBlob(pdf_buffer, "application/pdf")

  pdfium.pdfium.removeFunction(write_callback_pointer)
  pdfium.FPDF_CloseDocument(new_doc_pointer)

  return {
    blob,
    trim_box,
    media_box,
  }
}
